import {createContext, Dispatch, FC, PropsWithChildren, useCallback, useContext, useEffect, useState} from 'react';
import {useRecoilState, useSetRecoilState} from 'recoil';

import {EventBusContext} from '../EventBus';

import {getAuthUrl} from '../../api/api.config';
import axiosInstance from '../../api/non-protected';

import axiosInstanceV1 from '../../api/protected-v1';
import {useIsOnline} from '../../hooks/useIsOnline';
import {checkAuthorities} from '../../shared/helpers/checkAuthorities';
import {ToastHelpers} from '../../shared/helpers/toastHelpers';
import {TypeAccountRoles} from '../../shared/types/user-account.types';
import {authState} from '../../store/auth/atoms';
import {companiesForMapQueryTrigger, companiesQueryTrigger} from '../../store/company/atoms';
import {deviceTypesDictQueryTrigger} from '../../store/deviceTypes/atoms';

export const AuthContext = createContext<{
  loading?: boolean;
  checkLoginState?: () => Promise<void>;
  setLoading?: Dispatch<React.SetStateAction<boolean>>;
  apiAccess?: {loading: boolean; hasAccess: boolean};
}>({});

const AuthContextProvider: FC<PropsWithChildren> = ({children}) => {
  const [{isFirstLogin}, setAuthState] = useRecoilState(authState);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiAccess, setApiAccess] = useState({loading: true, hasAccess: false});
  const {bus} = useContext(EventBusContext);
  const triggerCompanies = useSetRecoilState(companiesQueryTrigger);
  const triggerCompaniesMap = useSetRecoilState(companiesForMapQueryTrigger);
  const triggerDeviceTypes = useSetRecoilState(deviceTypesDictQueryTrigger);

  const isOnline = useIsOnline();

  const checkApiAccess = useCallback(() => {
    axiosInstanceV1
      .get('/healthcheck')
      .then(() => {
        setApiAccess({hasAccess: true, loading: false});
      })
      .catch(() => {
        setApiAccess({hasAccess: false, loading: false});
      })
      .finally(() => {
        setApiAccess((prev) => ({...prev, loading: false}));
      });
  }, []);

  const checkLoginState = useCallback(async () => {
    try {
      setLoading(true);
      const {
        data: {loggedIn, user: userData},
      } = await axiosInstance.get(getAuthUrl('/logged_in'));

      const user = userData[0] || null;

      if (loggedIn && user) {
        triggerCompanies((v) => v + 1);
        triggerCompaniesMap((v) => v + 1);
        triggerDeviceTypes((v) => v + 1);

        setAuthState((prev) => ({...prev, user: user, isLoggedIn: loggedIn}));

        ToastHelpers.setIsAdmin(
          checkAuthorities(
            [TypeAccountRoles.ADMIN, TypeAccountRoles.FUNCTIONAL_ADMIN],
            user.roles as TypeAccountRoles[],
          ),
        );
      }
    } catch (err) {
      !isFirstLogin && bus?.emit('error', [err, {toastId: 'auth/logged_in'}]);
    } finally {
      isFirstLogin && setAuthState((prev) => ({...prev, isFirstLogin: false}));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstLogin]);

  useEffect(() => {
    let intervalId: NodeJS.Timer | null = null;
    if (isOnline) {
      !isFirstLogin && bus?.emit('isOnline', [isOnline]);
      checkApiAccess();

      intervalId = setInterval(checkApiAccess, 120000);
    } else bus?.emit('isOnline', [isOnline]);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    apiAccess.hasAccess && checkLoginState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiAccess.hasAccess]);

  return (
    <AuthContext.Provider value={{checkLoginState, loading, setLoading, apiAccess}}>{children}</AuthContext.Provider>
  );
};

export default AuthContextProvider;
